import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ReducersTypes } from "../redux/reducers/Models";
import { debtsPulling, requestDebts, setUser } from "../redux/ducks/user";
import { setCustomer } from "../redux/ducks/customer";
import {
  Debts,
  FormattedDebts,
  buildDebtsCart,
  isDebtAvailableForPayment,
} from "@/utils/transform";
import { resolveDebtsPullingResult } from "@/utils/resolveDebtsPullingResult";
import { getIsValidString } from "@/utils/functions";
import { PropsView } from "../pages/home/Models";

export const useDebts = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [shouldPoll, setShouldPoll] = useState(true);

  const user = useSelector(
    (
      state: ReducersTypes & {
        userReducer: ReducersTypes["userReducer"] & {
          cart: PropsView["selectables"];
          debts: FormattedDebts[];
        };
      },
    ) => state.userReducer,
  );

  const customer = useSelector(
    (
      state: ReducersTypes & {
        customerReducer: {
          client?: { id: string };
          vehicles?: { plate: string }[];
          debts?: FormattedDebts[];
          cart?: PropsView["selectables"];
          requestId?: string;
        };
      },
    ) => state.customerReducer,
  );

  const currentPlate = customer.selectedVehicles
    ? customer.selectedVehicles?.toString()
    : customer?.vehicles?.[0]?.plate;
  const currentDebts = customer?.debts || user?.debts;
  const currentCart = customer?.cart || user?.cart;
  const currentRequestId = customer?.requestId || user?.requestId;
  const debtsRef = useRef<FormattedDebts[]>(currentDebts || []);
  debtsRef.current = currentDebts || [];

  const persistDebts = (formattedDebts: FormattedDebts[]) => {
    if (customer?.client?.id) {
      dispatch(
        setCustomer({
          debts: formattedDebts,
          cart: buildDebtsCart(formattedDebts),
        }),
      );
    } else {
      dispatch(
        setUser({
          debts: formattedDebts,
          cart: buildDebtsCart(formattedDebts),
        }),
      );
    }
  };

  const handleDebtsPulling = (
    success: boolean,
    code?: string | Debts[],
    partialPassages?: Debts[],
  ) => {
    const result = resolveDebtsPullingResult({
      success,
      code,
      partialPassages,
      existingDebts: debtsRef.current,
    });

    if (result.kind === "partial") {
      persistDebts(result.debts);
      return;
    }

    if (result.kind === "retry") {
      return;
    }

    if (result.kind === "fail") {
      setIsLoading(false);
      setShouldPoll(false);
      persistDebts([]);
      return;
    }

    persistDebts(result.debts);
    setIsLoading(false);
    setShouldPoll(false);
  };

  const startPulling = (requestId?: string) => {
    if (!isLoading) setIsLoading(true);

    dispatch(
      debtsPulling({
        requestId: getIsValidString(requestId as string)
          ? (requestId as string)
          : (currentRequestId as string),
        callback: handleDebtsPulling,
      }),
    );
  };

  const handleRequestDebts = (success: boolean, code?: string) => {
    if (!success) {
      setIsLoading(false);
      setShouldPoll(false);

      if (customer?.client?.id) {
        dispatch(
          setCustomer({
            debts: [],
          }),
        );
      } else {
        dispatch(
          setUser({
            debts: [],
          }),
        );
      }
      return;
    }

    if (customer?.client?.id) {
      dispatch(
        setCustomer({
          requestId: code,
        }),
      );
    } else {
      dispatch(
        setUser({
          requestId: code,
        }),
      );
    }
    setShouldPoll(true);
  };

  const getDebts = async () => {
    if (isLoading) return;

    setIsLoading(true);
    setShouldPoll(true);

    dispatch(
      requestDebts({
        plate: currentPlate ?? "",
        callback: handleRequestDebts,
      }),
    );
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;

    if (name === "all") {
      const newCart =
        checked && currentDebts ? buildDebtsCart(currentDebts) : null;

      if (customer?.client?.id) {
        dispatch(
          setCustomer({
            cart: newCart,
          }),
        );
      } else {
        dispatch(
          setUser({
            cart: newCart,
          }),
        );
      }
      return;
    }

    const debt = currentDebts?.find((item) => item.id === name);
    if (debt && !isDebtAvailableForPayment(debt)) {
      return;
    }

    const updatedSelectables = currentCart
      ? [...currentCart.filter((item) => item.name !== name)]
      : [];

    if (checked) {
      updatedSelectables.push({ name, checked });
    }

    if (customer?.client?.id) {
      dispatch(
        setCustomer({
          cart: updatedSelectables.length ? updatedSelectables : null,
        }),
      );
    } else {
      dispatch(
        setUser({
          cart: updatedSelectables.length ? updatedSelectables : null,
        }),
      );
    }
  };

  useEffect(() => {
    if (
      getIsValidString(currentRequestId as string) &&
      isLoading &&
      shouldPoll
    ) {
      startPulling(currentRequestId as string);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRequestId, shouldPoll]);

  useEffect(() => {
    if (
      currentPlate ||
      (Array.isArray(customer.vehicles) && customer.vehicles.length === 0)
    )
      getDebts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPlate, customer?.client?.id, customer?.vehicles]);

  return {
    isLoading,
    debts: currentDebts,
    cart: currentCart,
    plate: currentPlate,
    onChange,
    getDebts,
  };
};
